/**
 * Claim routing rule hooks - Settings > Claims > Routing.
 *
 * Direct rules form the priority-ordered fallback chain applied at claim
 * creation; conditional rules re-route a claim when its state/conditions
 * match. The backend keeps the workflow side-effect wiring in sync on every
 * mutation, so these hooks only speak to /claims/routing-rules/.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import api from '@/admin/lib/api'
import type { PredicateNode } from '@/admin/components/condition-builder/types'

export type RoutingKind = 'direct' | 'conditional'
export type RoutingStrategy = 'least_busy' | 'round_robin'
export type RoutingAssigneeKind = 'role' | 'person' | 'zone'

export interface RoutingAssignee {
  kind: RoutingAssigneeKind
  value: string
  label?: string
}

export interface RoutingRule {
  uuid: string
  kind: RoutingKind
  key: string
  name: string
  description: string
  is_active: boolean
  priority: number
  assignee: RoutingAssignee
  strategy: RoutingStrategy | ''
  prefer_creator: boolean
  exclude_creator: boolean
  exclude_current_assignee: boolean
  notify: boolean
  /** Fill-only: apply only when the claim has no assignee; never overwrite. */
  only_if_unassigned: boolean
  conditions: PredicateNode | Record<string, never> | null
  trigger_on_creation: boolean
  trigger_states: string[]
  target_action_key: string
  /** Claim workflow this rule is scoped to; '' = all claim workflows. */
  workflow_key: string
  /** Runtime feature-flag gate (system-set, read-only). */
  feature_flag: string
  /** Platform producer that owns this rule (e.g. 'approval_policy'); when
   *  set the rule is read-only in this UI. Empty = admin-authored. */
  managed_by: string
  created_at: string
  updated_at: string
}

export interface RoutingRuleInput {
  kind: RoutingKind
  name: string
  description?: string
  is_active?: boolean
  assignee: { kind: RoutingAssigneeKind; value: string }
  strategy?: RoutingStrategy
  prefer_creator?: boolean
  exclude_creator?: boolean
  exclude_current_assignee?: boolean
  notify?: boolean
  only_if_unassigned?: boolean
  conditions?: PredicateNode | null
  trigger_on_creation?: boolean
  trigger_states?: string[]
  target_action_key?: string
  workflow_key?: string
}

export interface RoutingOption {
  value: string
  label: string
}

export interface RoutingStateOption {
  key: string
  label: string
  color: string
  is_initial: boolean
  is_terminal: boolean
}

export interface RoutingActionOption {
  key: string
  label: string
  source_state_key: string
}

export interface RoutingWorkflow {
  key: string
  label: string
  states: RoutingStateOption[]
  actions: RoutingActionOption[]
}

export interface RoutingMeta {
  roles: { name: string }[]
  strategy_options: RoutingOption[]
  state_options: RoutingStateOption[]
  action_options: RoutingActionOption[]
  /** Each claim workflow with its own states + actions. A rule scopes to
   *  one workflow; the dialogs show a workflow picker when there is >1. */
  workflows: RoutingWorkflow[]
  /** True when the tenant has geo-zone ownership configured; gates the
   *  'Zone owner' assignee option in the dialogs. */
  has_geo_zones: boolean
  /** Role names that own geo-zones (the valid zone assignee targets). */
  zone_roles: { name: string }[]
}

const KEY = ['claim-routing-rules'] as const
const META_KEY = ['claim-routing-meta'] as const

export function useRoutingRules() {
  return useQuery<RoutingRule[]>({
    queryKey: KEY,
    queryFn: () =>
      api.get('/claims/routing-rules/').then((r) => {
        const data = r.data?.results ?? r.data
        return Array.isArray(data) ? data : []
      }),
  })
}

export function useRoutingMeta() {
  return useQuery<RoutingMeta>({
    queryKey: META_KEY,
    queryFn: () => api.get('/claims/routing-rules/meta/').then((r) => r.data),
    staleTime: 60_000,
  })
}

export function useCreateRoutingRule() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: RoutingRuleInput) =>
      api.post('/claims/routing-rules/', data).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  })
}

export function useUpdateRoutingRule() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({
      uuid,
      ...data
    }: { uuid: string } & Partial<RoutingRuleInput>) =>
      api.patch(`/claims/routing-rules/${uuid}/`, data).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  })
}

export function useDeleteRoutingRule() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (uuid: string) => api.delete(`/claims/routing-rules/${uuid}/`),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  })
}

/**
 * Persist a new priority order for one kind's chain. Applies an optimistic
 * cache reorder immediately (drag feedback must be instant), rolling back on
 * error.
 */
export function useReorderRoutingRules() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (vars: { kind: RoutingKind; ordered_uuids: string[] }) =>
      api.post('/claims/routing-rules/reorder/', vars).then((r) => r.data),
    onMutate: async (vars) => {
      await qc.cancelQueries({ queryKey: KEY })
      const previous = qc.getQueryData<RoutingRule[]>(KEY)
      if (previous) {
        const total = vars.ordered_uuids.length
        const rank = new Map(vars.ordered_uuids.map((uuid, i) => [uuid, i]))
        qc.setQueryData<RoutingRule[]>(
          KEY,
          previous.map((rule) =>
            rule.kind === vars.kind && rank.has(rule.uuid)
              ? { ...rule, priority: (total - rank.get(rule.uuid)!) * 100 }
              : rule,
          ),
        )
      }
      return { previous }
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.previous) qc.setQueryData(KEY, ctx.previous)
    },
    onSettled: () => qc.invalidateQueries({ queryKey: KEY }),
  })
}
